import { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * A chip input for RULES, which are sentences, not tokens.
 *
 * WHY NOT ChipInput. That component splits every commit on `[\s,;\n]+`, which
 * is exactly right for the thing it was built for: hostnames, IPs and URLs,
 * where a space is a separator and never part of a value. Pointed at a rule it
 * is actively wrong. Typing "never touch /billing" produced three chips reading
 * `never`, `touch` and `/billing`, and the only reason the Rules of Engagement
 * lists currently hold readable sentences is that they were written through the
 * API rather than through this form.
 *
 * So: commit on Enter, never on a space. A comma is a legitimate character in a
 * sentence and no longer splits either. Pasting several lines still gives one
 * chip per LINE, because that is how a rule list arrives from a document.
 *
 * The suggestions are the other half. These fields read as "type a thing" and
 * an operator reasonably types the thing they have in their head, which on this
 * engagement was a hostname. Scope already says WHERE; these say WHAT, and a
 * list of examples is the cheapest way to teach that distinction at the moment
 * of use.
 */
export function RuleInput({
  values = [],
  onChange,
  suggestions = [],
  placeholder = 'Write a rule, then press Enter',
  disabled = false,
  variant = 'green',
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  const palette = variant === 'amber'
    ? { chip: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500',
        ring: 'focus-within:border-amber-400 focus-within:ring-amber-400/20' }
    : { chip: 'bg-wasabi-50 text-wasabi-800 border-wasabi-200', dot: 'bg-wasabi-500',
        ring: 'focus-within:border-wasabi-400 focus-within:ring-wasabi-400/20' };

  // Already-chosen rules drop out of the list: offering one twice is offering
  // the operator a no-op.
  const matches = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return suggestions
      .filter((s) => !values.includes(s))
      .filter((s) => !q || s.toLowerCase().includes(q))
      .slice(0, 6);
  }, [draft, suggestions, values]);

  const commit = (text) => {
    const rule = String(text).trim();
    if (!rule || disabled) return;
    if (!values.includes(rule)) onChange([...values, rule]);
    setDraft('');
    setOpen(false);
    setHighlight(0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' && matches.length) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp' && matches.length) {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // A highlighted suggestion wins, so Down-then-Enter picks it. Otherwise
      // whatever was typed is committed verbatim: these are free-form and the
      // suggestions are a convenience, never a whitelist.
      if (open && matches[highlight]) commit(matches[highlight]);
      else commit(draft);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !draft && values.length) {
      e.preventDefault();
      onChange(values.slice(0, -1));
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text || !text.includes('\n')) return;  // one line pastes normally
    e.preventDefault();
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    const next = [...values];
    for (const l of lines) if (!next.includes(l)) next.push(l);
    onChange(next);
    setDraft('');
  };

  return (
    <div className="relative">
      <div
        onClick={() => inputRef.current?.focus()}
        className={`min-h-[2.5rem] cursor-text rounded-lg border border-slate-300 bg-white px-2 py-1.5 transition focus-within:ring-2 ${palette.ring} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {values.map((v, i) => (
            <span
              key={`${v}-${i}`}
              className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${palette.chip}`}
            >
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${palette.dot}`} />
              <span className="truncate">{v}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onChange(values.filter((_, j) => j !== i)); }}
                  className="shrink-0 opacity-50 hover:opacity-100"
                  aria-label={`Remove rule: ${v}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          <input
            ref={inputRef}
            value={draft}
            disabled={disabled}
            onChange={(e) => { setDraft(e.target.value); setOpen(true); setHighlight(0); }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={() => setOpen(true)}
            // Committed on blur so a typed rule is never silently lost by
            // clicking Save, which is how a half-typed rule disappears.
            onBlur={() => { window.setTimeout(() => setOpen(false), 120); if (draft.trim()) commit(draft); }}
            placeholder={values.length ? '' : placeholder}
            className="min-w-[12rem] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-slate-400"
          />
        </div>
      </div>

      {open && matches.length > 0 && !disabled && (
        <ul className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <li className="border-b border-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Common rules, or keep typing your own
          </li>
          {matches.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                // onMouseDown, not onClick: blur fires first otherwise and the
                // list is gone before the click lands.
                onMouseDown={(e) => { e.preventDefault(); commit(s); }}
                onMouseEnter={() => setHighlight(i)}
                className={`block w-full px-3 py-1.5 text-left text-sm ${i === highlight ? 'bg-wasabi-50 text-wasabi-900' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Suggestions, grounded in what this platform actually does.
 *
 * Deliberately free of hostnames, paths and anything engagement-specific: the
 * point of the list is to demonstrate that these fields take RULES, and an
 * example containing a domain would teach the opposite.
 */
export const RULE_SUGGESTIONS = {
  allowed: [
    'Automated scanning and crawling',
    'Authenticated testing using the stored identities',
    'Cross-account access control testing by replaying one identity as another',
    'Injection testing with non-destructive payloads',
    'Read-only data extraction to demonstrate impact',
    'Brute force against test accounts we supplied',
    'Testing outside business hours',
  ],
  excluded: [
    'Denial of service or stress testing',
    'Social engineering or phishing of staff',
    'Physical intrusion',
    'Testing third-party or vendor-hosted services',
    'Password spraying against real user accounts',
    'Anything that modifies or deletes customer data',
  ],
  forbidden: [
    'No writes of any kind',
    'Do not create, modify or delete users',
    'Do not change passwords or rotate keys',
    'Do not send email from the application',
    'Do not log out the session this engagement depends on',
    'Stop and report before doing anything irreversible',
  ],
};
